import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  confirm,
  Input,
  Label,
  Separator,
} from "@devhop/ui";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";

export type EditableCampClassOption = {
  classTypeId: string;
  classTypeName: string;
  sortOrder: number;
  enabled: boolean;
  publicLabelLao: string;
  publicDescription: string;
  capacity: number;
  waitlistEnabled: boolean;
  waitlistCapacity: number | null;
  lowThreshold: number;
  criticalThreshold: number;
  claimed?: number;
  remaining?: number;
};

type CampEventClassOptionsEditorProps = {
  options: EditableCampClassOption[];
  availableClassTypes: Array<{ id: string; name: string }>;
  disabled?: boolean;
  onChange: (options: EditableCampClassOption[]) => void;
};

function SortableClassOptionRow({
  option,
  disabled,
  onChange,
  onRemove,
}: {
  option: EditableCampClassOption;
  disabled?: boolean;
  onChange: (patch: Partial<EditableCampClassOption>) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.classTypeId, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
  };

  const handleRemove = async () => {
    const ok = await confirm({
      title: "ລຶກຄລາສອອກຈາກ Camp",
      description: `ລຶກ "${option.classTypeName}" ອອກຈາກຟອມສາທານະ?`,
      actionText: "ລຶກ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) onRemove();
  };

  return (
    <Card ref={setNodeRef} style={style} className="overflow-hidden">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            className="mt-1 cursor-grab text-muted-foreground active:cursor-grabbing disabled:cursor-not-allowed"
            disabled={disabled}
            aria-label="ລາກຈັດລຳດັບ"
            {...attributes}
            {...listeners}
          >
            <GripVerticalIcon className="size-4" />
          </button>

          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">{option.classTypeName}</p>
                <p className="text-muted-foreground text-xs">
                  class_types · {option.classTypeId}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {typeof option.remaining === "number" ? (
                  <Badge variant="outline">
                    {option.claimed ?? 0}/{option.capacity} · ເຫຼືອ {option.remaining}
                  </Badge>
                ) : null}
                <Badge variant={option.enabled ? "default" : "secondary"}>
                  {option.enabled ? "ເປີດ" : "ປິດ"}
                </Badge>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded ? "ຫຍໍ້" : "ຕັ້ງຄ່າ"}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-destructive"
                  disabled={disabled}
                  onClick={handleRemove}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={option.enabled}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  onChange({ enabled: checked === true })
                }
              />
              <span>ເປີດໃຫ້ເລືອກໃນຟອມສາທານະ</span>
            </label>

            {expanded ? (
              <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`label-${option.classTypeId}`}>
                    ຊື່ສະແດງຜົນ (ລາວ) — ທາງເລືອກ
                  </Label>
                  <Input
                    id={`label-${option.classTypeId}`}
                    placeholder={option.classTypeName}
                    value={option.publicLabelLao}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({ publicLabelLao: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`desc-${option.classTypeId}`}>
                    ຄຳອະທິບາຍສາທານະ — ທາງເລືອກ
                  </Label>
                  <Input
                    id={`desc-${option.classTypeId}`}
                    placeholder="ຄຳອະທິບາຍສັ້ນໆ ສຳລັບຟອມ"
                    value={option.publicDescription}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({ publicDescription: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`cap-${option.classTypeId}`}>Capacity</Label>
                  <Input
                    id={`cap-${option.classTypeId}`}
                    type="number"
                    min={1}
                    value={option.capacity}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({
                        capacity: Math.max(1, Number(e.target.value) || 1),
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`wlcap-${option.classTypeId}`}>
                    Waitlist capacity
                  </Label>
                  <Input
                    id={`wlcap-${option.classTypeId}`}
                    type="number"
                    min={1}
                    placeholder="ບໍ່ຈຳກັດ"
                    value={option.waitlistCapacity ?? ""}
                    disabled={disabled}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      onChange({
                        waitlistCapacity: raw ? Math.max(1, Number(raw) || 1) : null,
                      });
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`low-${option.classTypeId}`}>
                    Low threshold (ເຕືອນເຫຼືອນ້ອຍ)
                  </Label>
                  <Input
                    id={`low-${option.classTypeId}`}
                    type="number"
                    min={0}
                    value={option.lowThreshold}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({
                        lowThreshold: Math.max(0, Number(e.target.value) || 0),
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`crit-${option.classTypeId}`}>
                    Critical threshold
                  </Label>
                  <Input
                    id={`crit-${option.classTypeId}`}
                    type="number"
                    min={0}
                    value={option.criticalThreshold}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({
                        criticalThreshold: Math.max(
                          0,
                          Number(e.target.value) || 0,
                        ),
                      })
                    }
                  />
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm sm:col-span-2">
                  <Checkbox
                    checked={option.waitlistEnabled}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                      onChange({ waitlistEnabled: checked === true })
                    }
                  />
                  <span>ເປີດຄິວສຳຮອງເມື່ອເຕັມ</span>
                </label>
              </div>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function CampEventClassOptionsEditor({
  options,
  availableClassTypes,
  disabled,
  onChange,
}: CampEventClassOptionsEditorProps) {
  const [pickId, setPickId] = useState("");

  const ordered = useMemo(
    () => [...options].sort((a, b) => a.sortOrder - b.sortOrder),
    [options],
  );

  const ids = useMemo(() => ordered.map((o) => o.classTypeId), [ordered]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const enabledCount = ordered.filter((o) => o.enabled).length;

  const addable = availableClassTypes.filter(
    (ct) => !ordered.some((o) => o.classTypeId === ct.id),
  );

  const syncSortOrder = (list: EditableCampClassOption[]) =>
    list.map((item, index) => ({ ...item, sortOrder: index }));

  const handleDragEnd = (evt: DragEndEvent) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(syncSortOrder(arrayMove(ordered, oldIndex, newIndex)));
  };

  const updateOption = (
    classTypeId: string,
    patch: Partial<EditableCampClassOption>,
  ) => {
    onChange(
      ordered.map((item) =>
        item.classTypeId === classTypeId ? { ...item, ...patch } : item,
      ),
    );
  };

  const removeOption = (classTypeId: string) => {
    onChange(syncSortOrder(ordered.filter((o) => o.classTypeId !== classTypeId)));
  };

  const addOption = () => {
    const picked = addable.find((ct) => ct.id === pickId);
    if (!picked) return;
    onChange(
      syncSortOrder([
        ...ordered,
        {
          classTypeId: picked.id,
          classTypeName: picked.name,
          sortOrder: ordered.length,
          enabled: true,
          publicLabelLao: "",
          publicDescription: "",
          capacity: 30,
          waitlistEnabled: true,
          waitlistCapacity: 50,
          lowThreshold: 10,
          criticalThreshold: 3,
        },
      ]),
    );
    setPickId("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          ເປີດສຳລັບຟອມ{" "}
          <span className="font-medium text-foreground">{enabledCount}</span> /{" "}
          {ordered.length} ຄລາສ
        </p>
        <p className="text-muted-foreground text-xs">
          ລາກບັດເພື່ອຈັດລຳດັບການສະແດງຜົນ
        </p>
      </div>

      {ordered.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີຄລາສ — ເພີ່ມຈາກປະເພດຄລາດສ໌ຂ້າງລຸ່ມ
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {ordered.map((option) => (
                <SortableClassOptionRow
                  key={option.classTypeId}
                  option={option}
                  disabled={disabled}
                  onChange={(patch) => updateOption(option.classTypeId, patch)}
                  onRemove={() => removeOption(option.classTypeId)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Separator />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label>ເພີ່ມຄລາສ</Label>
          <SimpleSelect
            value={pickId}
            onValueChange={setPickId}
            placeholder={
              addable.length > 0
                ? "ເລືອກປະເພດຄລາດສ໌..."
                : "ເພີ່ມຄລາສທັງໝົດແລ້ວ"
            }
            disabled={disabled || addable.length === 0}
            className="w-full"
            options={addable.map((ct) => ({
              value: ct.id,
              label: ct.name,
            }))}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || !pickId}
          onClick={addOption}
        >
          <PlusIcon className="mr-2 size-4" />
          ເພີ່ມ
        </Button>
      </div>
    </div>
  );
}
